import qs from 'qs';
import { openURL } from 'common/utils';
import { message as EeveeMessage } from '@fuxi/eevee-ui';
import {
  ApplyFilterEnum,
  BiddingListItem,
  BiddingQueryParams,
  BiddingTask,
  CommonListResponse,
  InviteMineBiddingTask,
  IPaginationParams,
  PaginationResult,
  RequirementListItem,
  SearchBidTaskDetail,
  ValidateApplyResponse,
} from 'common/types';
import { FastApiAxiosInstance } from 'common/service/axiosInstances';
import { TaskApplyParams } from 'common/types/task/apply';

const formatBiddingParams = (query: IPaginationParams<BiddingQueryParams>) => {
  const { isDelete, biddenStatus, _page, _num, ...rest } = query;
  biddenStatus !== 'ALL' && Object.assign(rest, { biddenStatus });
  isDelete !== 'ALL' && Object.assign(rest, { isDelete });

  return { ...rest, _page, _num } as IPaginationParams<BiddingQueryParams>;
};

const biddingTasks = {
  // 需求大厅的需求列表
  getRequirementList: (_page, _num, _query, requirementType, requirementSource, status) => {
    const params = {
      _page,
      _num,
      _query,
      requirementType,
      requirementSource,
      status,
    };
    return FastApiAxiosInstance.post<{ items: RequirementListItem[]; total: number }>(
      '/api/v1/bid/requirementList',
      params,
    );
  },
  searchBiddingTask: (params: { query?: string }) => {
    return FastApiAxiosInstance.get<SearchBidTaskDetail[]>('/api/v1/backend/bid/searchBiddingTask', {
      params,
    });
  },
  getCurrentAllocatorBiddingTask: async (query) => {
    const { data: biddingTasks } = await FastApiAxiosInstance.get<any>('/api/v1/bid/getCurrentAllocatorBiddingTask', {
      params: { query },
    });
    return biddingTasks;
  },
  // 需求详情页
  getRequirementDetail: (parentTaskId) => {
    return FastApiAxiosInstance.get<BiddingTask>('/api/v1/bid/requirementDetail', {
      params: { parentTaskId },
    });
  },
  getRequirementDetailInManager: async (parentTaskId) => {
    const params = { parentTaskId: Number(parentTaskId) };
    const { data } = await FastApiAxiosInstance.get<any, ExtendAxioResponse>('/api/v1/backend/bid/requirementDetail', {
      params,
    });
    return data;
  },
  // 撤销发布需求
  revokeRequirement: async (parentTaskId) => {
    const { data, code, message } = await FastApiAxiosInstance.post<any, ExtendAxioResponse>(
      '/api/v1/bid/revokeRequirement',
      { parentTaskId },
    );
    const hasError = code !== 0;
    if (hasError) {
      EeveeMessage.error(message || '失败');
    } else {
      EeveeMessage.success('需求撤销成功，你的需求已结束');
    }
    return code === 0;
  },
  // 申请领取需求
  addBid: async (requirementId, { quotation, description, attachment }) => {
    const { data, code, message } = await FastApiAxiosInstance.post<any, ExtendAxioResponse>('/api/v1/bid/addBidden', {
      requirementId: Number(requirementId),
      quotation: JSON.stringify(quotation),
      description,
      attachment,
    });
    const hasError = code !== 0;
    if (hasError) {
      EeveeMessage.error(message || '失败');
    } else {
      EeveeMessage.success('申请领取成功，可工作台查看');
    }
    return code === 0;
  },
  // 新申请领取需求
  applyBidden: async (params: TaskApplyParams) => {
    return FastApiAxiosInstance.post<null>('/api/v1/bid/addBidden', params);
  },
  // 申请详情
  getBiddenApplyDetail: async (biddenId) => {
    const params = { biddenId: Number(biddenId) };
    const resp = await FastApiAxiosInstance.get<any, ExtendAxioResponse>('/api/v1/bid/biddenApplyDetail', { params });
    return resp.data || {};
  },
  getBiddenApplyDetailInManager: async (biddenId) => {
    const params = { biddenId: Number(biddenId) };
    const resp = await FastApiAxiosInstance.get<any, ExtendAxioResponse>('/api/v1/backend/bid/biddenApplyDetail', {
      params,
    });
    return resp.data || {};
  },
  revokeBid: async (biddenId) => {
    const { data, code, message } = await FastApiAxiosInstance.post<any, ExtendAxioResponse>(
      '/api/v1/bid/revokeBidden',
      {
        biddenId,
      },
    );
    const hasError = code !== 0;
    if (hasError) {
      EeveeMessage.error(message || '失败');
    } else {
      EeveeMessage.success('你已成功撤销领取，该需求已结束');
    }
    return code === 0;
  },

  // 竞标管理列表的状态数量
  getBidStatusNum: async (role, _query) => {
    const params = { role, _query };
    const statusCounts = {};
    const { data } = await FastApiAxiosInstance.get<any, ExtendAxioResponse>('/api/v1/bid/biddenStatusNum', { params });
    data?.forEach(({ status, num }) => {
      Object.assign(statusCounts, { [status]: num });
    });
    return statusCounts;
  },
  // 竞标管理列表
  getBidList: async (role, status, _page, _num, _query) => {
    const params = { role, status, _page, _num, _query };
    const {
      data: { items, total },
    } = await FastApiAxiosInstance.get<any, ExtendAxioResponse>('/api/v1/bid/biddenList', { params });
    return { items, total } as { items: BiddingTask[]; total: number };
  },
  // 需求的竞标申请详情
  getBidDetailAndApplyList: async (requirementId, isInviting) => {
    const params = { requirementId, isInviting };
    const res = await FastApiAxiosInstance.get<any, ExtendAxioResponse>('/api/v1/bid/biddenApplyList', { params });
    const { data } = res;
    return data;
  },

  // 后台管理的竞标申请详情
  getBidDetailAndApplyListInManager: async (parentTaskId, isInviting) => {
    const params = { parentTaskId, isInviting };
    const res = await FastApiAxiosInstance.get<any, ExtendAxioResponse>('/api/v1/backend/bid/biddenApplyList', {
      params,
    });
    const { data } = res;
    return data;
  },

  // 查询企业竞标列表
  getEnterpriseBiddingList: (params: IPaginationParams<BiddingQueryParams>) => {
    return FastApiAxiosInstance.post<PaginationResult<BiddingListItem>>('/api/v1/backend/bid/enterprise/biddenList', {
      ...formatBiddingParams(params),
    });
  },

  // 查询个人竞标列表
  getIndividualBiddingList: (params: IPaginationParams<BiddingQueryParams>) => {
    return FastApiAxiosInstance.post<PaginationResult<BiddingListItem>>('/api/v1/backend/bid/individual/biddenList', {
      ...formatBiddingParams(params),
    });
  },

  // 后台管理-导出竞标列表
  exportBiddenList: async (query: BiddingQueryParams & { taskType: 'ENTERPRISE' | 'INDIVIDUAL' }) => {
    const { isDelete, biddenStatus, ...rest } = query;
    biddenStatus !== 'ALL' && Object.assign(rest, { biddenStatus });
    isDelete !== 'ALL' && Object.assign(rest, { isDelete });
    // 用qs序列化查询参数，如果是数组，用,拼接值
    const queryString = qs.stringify(rest, { arrayFormat: 'comma' });
    const url = `/fastApi/api/v1/backend/bid/biddenList/export?${queryString}`;
    openURL(url);
  },

  confirmProducer: async (params: {
    biddenId: number;
    reimbursementName?: string;
  }): Promise<ExtendAxioResponse<any>> => {
    const { biddenId, reimbursementName } = params;
    const resp = await FastApiAxiosInstance.post('/api/v1/bid/confirmProducer', {
      biddenId,
      reimbursementName,
    });
    return resp;
  },
  confirmProducerInManager: async (params: {
    biddenId: number;
    reimbursementName?: string;
  }): Promise<ExtendAxioResponse<any>> => {
    const { biddenId, reimbursementName } = params;
    const resp = await FastApiAxiosInstance.post<any, ExtendAxioResponse>('/api/v1/backend/bid/confirmProducer', {
      biddenId,
      reimbursementName,
    });
    return resp;
  },

  validateApply: (params: { parentTaskId: number }) => {
    return FastApiAxiosInstance.get<ValidateApplyResponse>('/api/v1/bid/applyValidate', { params });
  },

  // 删除企业竞标 /api/v1/backend/bid/deleteDemand
  deleteEnterpriseDemand: (params: { parentTaskId: number }) => {
    return FastApiAxiosInstance.post('/api/v1/backend/bid/deleteDemand', { ...params });
  },

  // 删除个人竞标 /api/v1/backend/bid/deleteC2CDemand
  deletePersonalDemand: (params: { parentTaskId: number }) => {
    return FastApiAxiosInstance.post('/api/v1/backend/bid/deleteC2CDemand', { ...params });
  },

  //获取邀请我的竞标任务列表
  getInviteMineBiddingTaskList: (params: { _page: number; _num: number; _nextId?: number; _query?: string }) => {
    return FastApiAxiosInstance.get<CommonListResponse<InviteMineBiddingTask>>('/api/v1/bid/invitedList', { params });
  },

  //忽略邀请我的竞标任务
  ignoreInviteMineBiddingTask: (params: { biddenId: number }) => {
    return FastApiAxiosInstance.post('/api/v1/bid/ignoreInvite', params);
  },

  // 取消预选
  cancelPreSelect: (params: { biddenId: number }) => {
    return FastApiAxiosInstance.post('/api/v1/bid/cancelPreSelect', params);
  },
};

export default biddingTasks;
